import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { getPlatformRepasseRequests, patchPlatformRepasseRequest } from "../../../api/passagensApi";
import { AnalyticsDataTable, AnalyticsSection, useAnalyticsTableSort } from "./platformConsoleAnalyticsUi";
import { BusyButton } from "./shared";

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDateTime(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("pt-BR");
}

const STATUS_COLOR = {
  pendente: "warning",
  em_processamento: "info",
  pago: "success",
  rejeitado: "error",
  cancelado: "default",
};

const REPASSE_SORT_FIELDS = {
  workspace: { type: "string", get: (r) => r.dominio || r.dominio_id },
  date: { type: "number", get: (r) => new Date(r.solicitado_em || 0).getTime() || 0 },
  value: { type: "number", get: (r) => r.valor_solicitado },
  status: { type: "string", get: (r) => r.status },
  note: { type: "string", get: (r) => r.observacao_cliente },
};

export default function PassengerRepasseRequestsPanel({ dominioIdFilter = null }) {
  const { t } = useTranslation("app");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pendentes: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionRow, setActionRow] = useState(null);
  const [actionStatus, setActionStatus] = useState("pago");
  const [observacao, setObservacao] = useState("");
  const [comprovante, setComprovante] = useState("");
  const [saving, setSaving] = useState(false);

  const {
    sortBy: repasseSortBy,
    sortOrder: repasseSortOrder,
    handleSort: handleRepasseSort,
    sortRows: sortRepasseRows,
  } = useAnalyticsTableSort("passenger-repasse", REPASSE_SORT_FIELDS, { sortBy: "date", sortOrder: "desc" });

  const sortedRows = useMemo(() => sortRepasseRows(rows), [rows, sortRepasseRows]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 100 };
      if (dominioIdFilter) params.dominioId = dominioIdFilter;
      const data = await getPlatformRepasseRequests(params);
      setRows(
        (data?.rows || []).map((row) => ({
          ...row,
          _rowKey: String(row.id),
        }))
      );
      setSummary(data?.summary || { pendentes: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("platformPage.repasseRequestsLoadError"));
    } finally {
      setLoading(false);
    }
  }, [dominioIdFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openAction = (row, status) => {
    setActionRow(row);
    setActionStatus(status);
    setObservacao("");
    setComprovante("");
  };

  const submitAction = async () => {
    if (!actionRow) return;
    setSaving(true);
    try {
      await patchPlatformRepasseRequest(actionRow.id, {
        status: actionStatus,
        observacao_plataforma: observacao,
        comprovante_ref: comprovante,
      });
      setActionRow(null);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || t("platformPage.repasseRequestsActionError"));
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
        {summary.pendentes > 0 ? (
          <Chip
            color="warning"
            variant="outlined"
            label={t("platformPage.repasseRequestsPendingChip", { count: summary.pendentes })}
          />
        ) : (
          <Box />
        )}
        <BusyButton size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} loading={loading}>
          {t("billingPage.refresh")}
        </BusyButton>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>{error}</Alert> : null}

      {loading && !rows.length ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <AnalyticsDataTable
          rows={sortedRows}
          emptyLabel={t("platformPage.repasseRequestsEmpty")}
          sortBy={repasseSortBy}
          sortOrder={repasseSortOrder}
          onSort={handleRepasseSort}
          sortFields={REPASSE_SORT_FIELDS}
          columns={[
            {
              id: "workspace",
              label: t("platformPage.productUsageColWorkspace"),
              render: (row) => row.dominio || row.dominio_id,
            },
            {
              id: "date",
              label: t("passagensRepasses.colDate"),
              render: (row) => fmtDateTime(row.solicitado_em),
            },
            {
              id: "value",
              label: t("passagensRepasses.colValue"),
              align: "right",
              render: (row) => fmtMoney(row.valor_solicitado),
            },
            {
              id: "status",
              label: t("passagensRepasses.colStatus"),
              render: (row) => <Chip size="small" variant="outlined" color={STATUS_COLOR[row.status] || "default"} label={row.status} />,
            },
            {
              id: "note",
              label: t("passagensRepasses.colNote"),
              render: (row) => row.observacao_cliente || "—",
            },
            {
              id: "actions",
              label: t("platformPage.repasseRequestsColActions"),
              align: "right",
              render: (row) => (
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  {row.status === "pendente" ? (
                    <>
                      <Button size="small" onClick={() => openAction(row, "em_processamento")}>
                        {t("platformPage.repasseProcess")}
                      </Button>
                      <Button size="small" color="error" onClick={() => openAction(row, "rejeitado")}>
                        {t("platformPage.repasseReject")}
                      </Button>
                    </>
                  ) : null}
                  {row.status === "em_processamento" ? (
                    <Button size="small" variant="contained" onClick={() => openAction(row, "pago")}>
                      {t("platformPage.repasseMarkPaid")}
                    </Button>
                  ) : null}
                </Stack>
              ),
            },
          ]}
        />
      )}

      <Dialog open={Boolean(actionRow)} onClose={() => setActionRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionStatus === "pago"
            ? t("platformPage.repasseMarkPaid")
            : actionStatus === "rejeitado"
              ? t("platformPage.repasseReject")
              : t("platformPage.repasseProcess")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {actionRow?.dominio} — {fmtMoney(actionRow?.valor_solicitado)}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t("passagensRepasses.colNote")}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            sx={{ mb: 2 }}
          />
          {actionStatus === "pago" ? (
            <TextField
              fullWidth
              label={t("platformPage.repasseComprovante")}
              value={comprovante}
              onChange={(e) => setComprovante(e.target.value)}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionRow(null)}>{t("common.cancel", { defaultValue: "Cancelar" })}</Button>
          <Button variant="contained" onClick={submitAction} disabled={saving}>
            {t("common.confirm", { defaultValue: "Confirmar" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <AnalyticsSection title={t("platformPage.repasseRequestsTitle")} hint={t("platformPage.repasseRequestsIntro")} loading={loading && rows.length > 0}>
      {content}
    </AnalyticsSection>
  );
}
